import {
  Button,
  Collapse,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { Fade, FileUploader, IconButton, Resource } from 'utoniq-ui'

import { ItemThumbnailType, ItemType } from '@/clients/utoniq-core/schema'
import {
  CreateItemProps,
  FileUploadInfo,
} from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type ItemThumbnailInputCellProps = {
  watch: UseFormWatch<CreateItemProps>
  setValue: UseFormSetValue<CreateItemProps>
  error?: FieldError
  errorBack?: FieldError
  itemUploadInfo: FileUploadInfo
  onClose?: () => void
  onBackClose?: () => void
  resourceType: ItemType
  isEdit?: boolean
}
export const ItemThumbnailInputCell: Cell<ItemThumbnailInputCellProps> = ({
  watch,
  error,
  errorBack,
  setValue,
  itemUploadInfo,
  onClose,
  onBackClose,
  resourceType,
  isEdit,
}) => {
  const { t } = useTranslation('consoleItemCreate')
  const thumbnailType = watch('thumbnailType')
  const [expanded, setExpanded] = useState<boolean>(isEdit ? true : false)
  const options = useMemo<
    {
      value:
        | ItemThumbnailType.Rectangle
        | ItemThumbnailType.Square
        | ItemThumbnailType.Passthrough
      label: string
    }[]
  >(
    () =>
      resourceType === ItemType.Avatar
        ? [{ value: ItemThumbnailType.Passthrough, label: '' }]
        : [
            { value: ItemThumbnailType.Square, label: t('square') },
            { value: ItemThumbnailType.Rectangle, label: t('rectangle') },
          ],
    [resourceType, t]
  )
  return (
    <CellFrame sx={{ pb: 8 }}>
      <Typography variant="h5">{t('thumbnailLabel')}</Typography>
      {resourceType !== ItemType.Avatar && (
        <Stack direction="row" spacing={2} sx={{ py: 2, mt: 2 }}>
          {options.map((option, index) => (
            <Button
              key={index}
              variant={
                thumbnailType === option.value ? 'contained' : 'outlined'
              }
              onClick={() => setValue('thumbnailType', option.value)}
              /**
               * NOTE:
               * アイテムのサムネイル型が変更できるようにAPI修正するまで編集UIをdisabledにしている
               * https://app.clickup.com/t/908127/UT-14167
               */
              disabled={isEdit && thumbnailType !== option.value}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
      )}
      {options.map(
        ({ value }) =>
          value === thumbnailType && (
            <Fade key={value}>
              <FileUploader
                t={t}
                sx={{ p: 0 }}
                shape={value === 'square' ? 'square' : 'rectangle'}
                resource={watch('thumbnailResource') as Resource | undefined}
                fileUploadInfo={{
                  type: 'image',
                  onDrop() {
                    return itemUploadInfo
                      .onDrop(ItemType.Image)
                      .then(async resource => {
                        if (!resource?.imageUploadPath) {
                          return
                        }
                        setValue('thumbnail', resource.imageUploadPath, {
                          shouldValidate: true,
                        })
                        return resource
                      })
                  },
                  progress: itemUploadInfo.progress || 0,
                  description: t(`${value}ThumbnailUploaderDescription`),
                  onClose() {
                    setValue('thumbnail', '', {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                    setValue('thumbnailResource', null)
                    onClose && onClose()
                  },
                }}
                content={{ title: '' }}
              />
            </Fade>
          )
      )}
      {!!error && <FormHelperText error>{error.message}</FormHelperText>}
      <Stack direction="row" alignItems="center" sx={{ pt: 4 }}>
        <Typography variant="h6">{t('thumbnailBackLabel')}</Typography>
        <Typography variant="subtitle1" color="text.hint">
          {t('optional')}
        </Typography>
        {expanded ? (
          <IconButton.UpArrow
            size="medium"
            onClick={() => setExpanded(prevState => !prevState)}
            bgcolor="transparent"
          />
        ) : (
          <IconButton.DownArrow
            size="medium"
            onClick={() => setExpanded(prevState => !prevState)}
            bgcolor="transparent"
          />
        )}
      </Stack>
      <Typography
        variant="subtitle1"
        color="text.hint"
        sx={{ size: '11px', lineHeight: '16px', fontWeight: 300 }}
      >
        {t('thumbnailBackCaution')}
      </Typography>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {options.map(
          ({ value }) =>
            value === thumbnailType && (
              <Fade key={value}>
                <FileUploader
                  t={t}
                  sx={{ p: 0 }}
                  shape={value === 'square' ? 'square' : 'rectangle'}
                  resource={
                    watch('thumbnailBackResource') as Resource | undefined
                  }
                  fileUploadInfo={{
                    type: 'image',
                    onDrop() {
                      return itemUploadInfo
                        .onDrop(ItemType.Image)
                        .then(async resource => {
                          if (!resource) {
                            return
                          }
                          setValue('thumbnailBack', resource.imageUploadPath, {
                            shouldValidate: true,
                          })
                          setValue('isBothThumbnail', false)
                          return resource
                        })
                    },
                    progress: itemUploadInfo.progress || 0,
                    description: t(`${value}ThumbnailUploaderDescription`),
                    onClose() {
                      setValue('thumbnailBack', '', {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                      setValue('thumbnailBackResource', null)
                      setValue('isBothThumbnail', isEdit ? true : false)
                      onBackClose && onBackClose()
                    },
                  }}
                  content={{ title: '' }}
                />
              </Fade>
            )
        )}
        {!!errorBack && (
          <FormHelperText error>{errorBack.message}</FormHelperText>
        )}
      </Collapse>
    </CellFrame>
  )
}
